import React, { FC, createRef, useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import type { FormInstance } from 'antd/es/form'
import {
    Space, Form, Button, notification, Switch, Input
} from 'antd'
import { SmileOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import type { IAppState } from '@/store/reducers'
import type { IEditProjectInfoApply } from '@/store/models/tableEdit.model'
import { editProjectInfoAppr, getProjectInfoApplyList } from '@/services/table.service'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import { getDicClass } from '@/pages/charts'
import DiffComp from '@/pages/table2Mana/components/diffComp'
import Nav from '@/components/nav'

const popMsg = (msg: string) => {
    const args = {
        message: msg,
        description: '',
        icon: <SmileOutlined style={{ color: '#e1931e' }} />,
        duration: 3,
    }
    notification.open(args)
}
const TableEditAppr: FC = () => {
    const dispatch = useDispatch()
    const locationObj = useLocation()
    const { t, i18n } = useTranslation()
    const history: any = useHistory()
    const route = useSelector((state: IAppState) => state.router)
    const dicClassList = useSelector((state: IAppState) => state.tableEditData.dicClass)
    const { TextArea } = Input

    const formRef = createRef<FormInstance>()
    const locationSearch = useRef<{
        workGroupId: number | undefined
        applyId: number | undefined
        isView: boolean
    }>({
        workGroupId: undefined,
        applyId: undefined,
        isView: false
    })

    // 表单数据
    const [formDataObj, setFormDataObj] = useState<any>(null)

    const layout = {
        labelCol: { span: 5 },
        wrapperCol: { span: 16 },
    }
    const tailLayout = {
        wrapperCol: { offset: 6, span: 10 },
    }
    
    const onFinish = async (values: any) => {
        console.log('onFinish: ', values)
        const reqData: IEditProjectInfoApply = {
            auditComments: values.auditComments,
            auditStatus: values.auditStatus ? 2 : 3,
            changeFields: JSON.parse(formDataObj.changeFields),
            techProjectId: values.techProjectId
        }
        console.log('onFinish: ', values, reqData)
        try {
            await editProjectInfoAppr(reqData, locationSearch.current.applyId)
            const args = {
                message: t('save') + (i18n.language !== 'zh' ? ' ' : '') + t('success'),
                description: '',
                icon: <CheckCircleOutlined style={{ color: '#08993f' }} />,
                duration: 3,
            }
            notification.open(args)
            if (locationObj.key) {
                history.goBack()
            } else {
                history.replace('/indexPage')
            }
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }
    const onFinishFailed = ({ errorFields }: { errorFields: any}) => {
        console.log('onFinishFailed: ', errorFields)
        if (errorFields.length) {
            popMsg(t('FinishFailed'))
        }
    }
    const onReset = () => {
        formRef.current?.resetFields()
    }

    const getList = async ({
        workGroupId,
        applyId
    }: any) => {
        try {
            const res = await getProjectInfoApplyList({
                pageNo: 1,
                applyId,
                workGroupId
            })
            console.log('res.content: ', res.content)
            const findObj = res.content.find((item: any) => item.id === applyId)
            setFormDataObj(findObj)
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }

    const routes = [
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/projectInfoChangeApplyList',
            breadcrumbName: t('approvalList'),
        },
        {
            path: '/projectInfoEdit',
            breadcrumbName: locationSearch.current.isView ? t('approvalView') : t('approvalEdit'),
        },
    ]

    useEffect(() => {
        console.log('location: ', locationObj)
        const routeParams = new URLSearchParams(location.search)
        const worGroupIdParam = routeParams.get('workGroupId')
        const applyId = routeParams.get('applyId')
        const isView = routeParams.get('isView')
        locationSearch.current = {
            workGroupId: +(worGroupIdParam || ''),
            applyId: +(applyId || ''),
            isView: !!isView
        }

        // 获取字典列表，作为下拉选项
        if (Object.keys(dicClassList).length === 0) {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
            getDicClass(dispatch, (a: boolean): void => {})
        }

        // 获取变更申请列表
        getList({
            workGroupId: locationSearch.current.workGroupId,
            applyId: locationSearch.current.applyId
        })
    }, [])
    useEffect(() => {
        if (!formDataObj) return
        // 初始化 workgroupId
        formRef.current?.setFieldsValue({
            ...formDataObj,
            workgroupId: locationSearch.current.workGroupId,
            techProjectId: formDataObj.techProject.id
        })
    }, [formDataObj])
    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                route={route}
                dispatch={dispatch}
            />
            <Form {...layout} ref={formRef} name="control-ref"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                style={{ padding: '20px 0 10px', backgroundColor: '#fff' }}
            >
                <Form.Item name="auditComments" label={t('auditComments')}
                    rules={[{ required: true, message: t('auditComments Required') }]}
                >
                    {!locationSearch.current.isView 
                        ? <TextArea /> 
                        : (formDataObj ? formDataObj.auditComments : '')
                    }
                </Form.Item>
                <Form.Item name="auditStatus" label={t('isPassed')}
                    valuePropName="checked"
                >
                    {!locationSearch.current.isView
                        ? <Switch /> 
                        : (formDataObj && [t('auditWait'), t('auditPass'), t('auditUnPass')][formDataObj.auditStatus - 1])
                    }
                </Form.Item>
                {locationSearch.current.isView && formDataObj && <Form.Item
                    style={{ height: 0, margin: 0 }}
                >
                    <div className="stamp">
                        <div className={`lbpmProcessStatus ${formDataObj.auditStatus === 3 ? 'lbpmDiscardStatus' : ''}`}>
                            <div className="lbpmProcessText">
                                {[t('auditWait'), t('auditPass'), t('auditUnPass')][formDataObj.auditStatus - 1]}
                            </div>
                        </div>
                    </div>
                </Form.Item>}
                {!locationSearch.current.isView && (
                    <Form.Item {...tailLayout} style={{ textAlign: 'center' }}>
                        <Space size={20}>
                            <Button type="primary" htmlType="submit">{t('submit')}</Button>
                            {locationObj.pathname.includes('Add') && <Button htmlType="button" onClick={onReset}>{t('reset')}</Button>}
                        </Space>
                    </Form.Item>)
                }
            </Form>
            {formDataObj && <DiffComp dataObj={formDataObj} />}
        </Space>
    )
}

export default TableEditAppr
